import Screen from '../Screen';
import React, { FC, useState, useEffect } from 'react';
import styled from 'styled-components/native';
import Fonts from 'theme/fonts';
import Colors from 'theme/colors';
import { useParams, useHistory } from 'react-router-dom';
import SubtleAd from 'components/SubtleAd';
import SelectCard from 'components/SelectCard';
import manifestAPI from 'api/manifestAPI';
import { PARTS, getPartMeta } from 'config/toeicMeta';

const Intro = styled.View`
  background-color: ${Colors.WHITE};
  border-radius: 12px;
  border-width: 1px;
  border-color: ${Colors.CARD_BORDER};
  padding: 14px 16px;
  margin: 4px 8px 12px;
`;

const IntroText = styled(Fonts.Normal)`
  font-size: 14px;
  color: ${Colors.MUTED_TEXT};
  line-height: 20px;
`;

const SectionTitle = styled(Fonts.Bold)`
  font-size: 15px;
  color: ${Colors.MAIN_COLOR};
  margin: 12px 12px 4px;
`;

const SectionHint = styled(Fonts.Normal)`
  font-size: 13px;
  color: ${Colors.MUTED_TEXT};
  margin: 0 12px 6px;
`;

const PartList: FC = () => {
  const { testNumber, year } = useParams();
  const history = useHistory();
  const currentYear = year || '2020';

  const [manifestParts, setManifestParts] = useState<number[]>(PARTS.map((p) => p.part));

  useEffect(() => {
    let cancelled = false;
    manifestAPI.getYear(currentYear)
      .then((manifest: any) => {
        if (cancelled) return;
        const tests = (manifest && manifest.tests) || [];
        const entry = tests.filter((t: any) => `${t.test}` === `${testNumber}`)[0];
        const parts = (entry && entry.parts) || [];
        setManifestParts(parts.length ? parts.map(Number) : PARTS.map((p) => p.part));
      })
      .catch(() => {
        if (!cancelled) setManifestParts(PARTS.map((p) => p.part));
      });
    return () => { cancelled = true; };
  }, [currentYear, testNumber]);

  const openPartScreen = (partNumber: number) => {
    history.push(`/toeic${currentYear}/test${testNumber}/part${partNumber}`);
  };

  const listening = manifestParts
    .map((n) => getPartMeta(n))
    .filter((p): p is NonNullable<typeof p> => !!p && p.section === 'listening');
  const reading = manifestParts
    .map((n) => getPartMeta(n))
    .filter((p): p is NonNullable<typeof p> => !!p && p.section === 'reading');

  const renderPart = (meta: NonNullable<ReturnType<typeof getPartMeta>>) => (
    <SelectCard
      key={meta.part}
      title={`Part ${meta.part} · ${meta.en}`}
      subtitle={meta.vi}
      meta={meta.countLabel}
      badge={meta.section === 'listening' ? 'Nghe' : 'Đọc'}
      onPress={() => openPartScreen(meta.part)}
    />
  );

  return (
    <Screen canBack title={`Test ${testNumber} · TOEIC ${currentYear}`}>
      <Intro>
        <IntroText>
          Chọn Part theo kỹ năng bạn muốn ôn. Mỗi Part có hướng dẫn ngắn khi vào làm bài.
          Nộp bài để xem số câu đúng và đáp án.
        </IntroText>
      </Intro>

      {listening.length > 0 && (
        <>
          <SectionTitle>Listening · Nghe hiểu</SectionTitle>
          <SectionHint>Part 1–4 · làm lần lượt hoặc chọn Part yếu để ôn riêng</SectionHint>
          {listening.map(renderPart)}
        </>
      )}

      {reading.length > 0 && (
        <>
          <SectionTitle>Reading · Đọc hiểu</SectionTitle>
          <SectionHint>Part 5–7 · luyện ngữ pháp, từ vựng và đọc hiểu</SectionHint>
          {reading.map(renderPart)}
        </>
      )}

      <SubtleAd />
    </Screen>
  );
};

export default PartList;
